/**
 * Operator URL segments from ABP tenancy name (e.g. "oneok") for /operators/:slug/...
 */

export type OperatorSlugRow = {
  operatorId: string
  /** ABP tenant name — sole source for the URL segment. */
  tenancyName: string
}

const SLUG_SAFE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function tenancyNameToUrlSegment(tenancyName: string): string {
  return tenancyName.trim().toLowerCase()
}

function disambiguatedSlug(base: string, operatorId: string, salt: number): string {
  const tail = operatorId.replace(/\D/g, "").slice(-6) || operatorId.slice(-6)
  const b = base || "operator"
  return salt <= 0 ? `${b}-${tail}` : `${b}-${tail}-${salt}`
}

/**
 * Stable slug per operatorId from tenancy name. Rows without tenancyName are omitted.
 */
export function buildOperatorSlugMaps(rows: readonly OperatorSlugRow[]): {
  idToSlug: Map<string, string>
  slugToId: Map<string, string>
} {
  const sorted = [...rows].sort((a, b) => a.operatorId.localeCompare(b.operatorId))
  const slugToId = new Map<string, string>()
  const idToSlug = new Map<string, string>()

  for (const row of sorted) {
    const raw = row.tenancyName.trim()
    if (!raw) continue
    const base = tenancyNameToUrlSegment(raw)
    if (!base || !SLUG_SAFE.test(base)) continue
    let slug = base
    let salt = 0
    while (slugToId.has(slug) && slugToId.get(slug) !== row.operatorId) {
      slug = disambiguatedSlug(base, row.operatorId, salt)
      salt++
    }
    idToSlug.set(row.operatorId, slug)
    slugToId.set(slug, row.operatorId)
  }

  return { idToSlug, slugToId }
}

export function resolveOperatorSegmentToId(
  segment: string,
  slugToId: ReadonlyMap<string, string>,
): string | null {
  const s = segment.trim()
  if (!s) return null
  if (/^\d+$/.test(s)) return s
  return slugToId.get(s.toLowerCase()) ?? null
}
